import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useTheme } from '../ui/theme-provider';
import { cn } from '../../lib/utils';
import { AlertTriangle, MapPin, Calendar, Briefcase } from 'lucide-react';

const JobEditModal = ({ job, isOpen, onClose, onSave }) => {
  const { theme } = useTheme();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    location: '',
    employmentType: '',
    salary: '',
    practiceInfo: '',
    applicationInstructions: ''
  });
  const [saving, setSaving] = useState(false);

  // Populate form when job changes
  useEffect(() => {
    if (job) {
      setFormData({
        title: job.title || '',
        description: job.description || '',
        location: job.location || '',
        employmentType: job.employmentType || '',
        salary: job.salary || '',
        practiceInfo: job.practiceInfo || '',
        applicationInstructions: job.applicationInstructions || ''
      });
    }
  }, [job]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(formData);
    } finally {
      setSaving(false);
    }
  };

  const employmentTypes = ['Full-time', 'Part-time', 'NHS', 'Contract', 'Locum', 'Temporary', 'Permanent'];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={cn(
        "max-w-2xl max-h-[90vh] overflow-y-auto",
        theme === 'light' ? 'bg-white text-black' : 'bg-[#2A2A3E] border-purple-500/20 text-white'
      )}>
        <DialogHeader>
          <DialogTitle className={cn(
            "text-xl font-bold flex items-center gap-2",
            theme === 'light' ? 'text-gray-900' : 'text-white'
          )}>
            Edit RSS Job
            {job?.needsLocationReview && (
              <AlertTriangle className="h-5 w-5 text-orange-500" />
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Job ID and Source Info */}
          <div className={cn(
            "p-3 rounded-lg text-sm",
            theme === 'light' ? 'bg-gray-50' : 'bg-gray-800'
          )}>
            <div className="flex justify-between items-center">
              <span>Job ID: {job?.id}</span>
              <span>Source: {job?.source}</span>
            </div>
            {job?.scrapedAt && (
              <div className="text-xs text-muted-foreground mt-1">
                Scraped: {new Date(job.scrapedAt.seconds * 1000).toLocaleString()}
              </div>
            )}
          </div>

          {/* Location Alert */}
          {job?.needsLocationReview && (
            <div className={cn(
              "p-3 rounded-lg border",
              theme === 'light' 
                ? 'bg-orange-50 border-orange-200 text-orange-800'
                : 'bg-orange-900/20 border-orange-800 text-orange-200'
            )}>
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" />
                <span className="font-medium">Location Review Needed</span>
              </div>
              <p className="text-sm mt-1">
                This job needs location verification. Please update the location field and it will be automatically geocoded.
              </p>
            </div>
          )}

          {/* Edit Form */}
          <div className="space-y-4">
            {/* Title */}
            <div>
              <Label className={cn(
                "text-sm font-medium",
                theme === 'light' ? 'text-gray-700' : 'text-gray-200'
              )}>
                Job Title
              </Label>
              <Input
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                className={cn(
                  theme === 'light' 
                    ? 'bg-white border-gray-300 text-black'
                    : 'bg-[#1F1F2E] border-gray-700 text-white'
                )}
              />
            </div>

            {/* Location - Highlighted if needs review */}
            <div>
              <Label className={cn(
                "text-sm font-medium flex items-center gap-1",
                job?.needsLocationReview 
                  ? 'text-orange-600' 
                  : theme === 'light' ? 'text-gray-700' : 'text-gray-200'
              )}>
                <MapPin className="h-4 w-4" />
                Location {job?.needsLocationReview && <span className="text-red-500">*</span>}
              </Label>
              <Input
                value={formData.location}
                onChange={(e) => handleInputChange('location', e.target.value)}
                placeholder="e.g., London, Belfast, Manchester, etc."
                className={cn(
                  job?.needsLocationReview 
                    ? 'border-orange-300 focus:border-orange-500'
                    : '',
                  theme === 'light' 
                    ? 'bg-white border-gray-300 text-black'
                    : 'bg-[#1F1F2E] border-gray-700 text-white'
                )}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Will be automatically geocoded when saved
              </p>
            </div>

            {/* Employment Type */}
            <div>
              <Label className={cn(
                "text-sm font-medium",
                theme === 'light' ? 'text-gray-700' : 'text-gray-200'
              )}>
                Employment Type
              </Label>
              <Select value={formData.employmentType} onValueChange={(value) => handleInputChange('employmentType', value)}>
                <SelectTrigger className={cn(
                  theme === 'light' 
                    ? 'bg-white border-gray-300 text-black'
                    : 'bg-[#1F1F2E] border-gray-700 text-white'
                )}>
                  <SelectValue placeholder="Select employment type" />
                </SelectTrigger>
                <SelectContent>
                  {employmentTypes.map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Salary */}
            <div>
              <Label className={cn(
                "text-sm font-medium",
                theme === 'light' ? 'text-gray-700' : 'text-gray-200'
              )}>
                Salary
              </Label>
              <Input
                value={formData.salary}
                onChange={(e) => handleInputChange('salary', e.target.value)}
                placeholder="e.g., £45 per hour, £25,000 - £35,000"
                className={cn(
                  theme === 'light' 
                    ? 'bg-white border-gray-300 text-black'
                    : 'bg-[#1F1F2E] border-gray-700 text-white'
                )}
              />
            </div>

            {/* Practice Info */}
            <div>
              <Label className={cn(
                "text-sm font-medium",
                theme === 'light' ? 'text-gray-700' : 'text-gray-200'
              )}>
                Practice/Company
              </Label>
              <Input
                value={formData.practiceInfo}
                onChange={(e) => handleInputChange('practiceInfo', e.target.value)}
                placeholder="e.g., Bupa Dental Care, Private Practice"
                className={cn(
                  theme === 'light' 
                    ? 'bg-white border-gray-300 text-black'
                    : 'bg-[#1F1F2E] border-gray-700 text-white'
                )}
              />
            </div>

            {/* Description */}
            <div>
              <Label className={cn(
                "text-sm font-medium",
                theme === 'light' ? 'text-gray-700' : 'text-gray-200'
              )}>
                Job Description
              </Label>
              <Textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                rows={6}
                className={cn(
                  theme === 'light' 
                    ? 'bg-white border-gray-300 text-black'
                    : 'bg-[#1F1F2E] border-gray-700 text-white'
                )}
              />
            </div>
          </div>

          {/* Current Geocoding Status */}
          {job && (
            <div className={cn(
              "p-3 rounded-lg text-sm",
              theme === 'light' ? 'bg-gray-50' : 'bg-gray-800'
            )}>
              <h4 className="font-medium mb-2">Current Geocoding Status:</h4>
              <div className="space-y-1 text-xs">
                <div>Postcode: {job.postcode || 'Not found'}</div>
                <div>Coordinates: {job.latitude && job.longitude ? `${job.latitude}, ${job.longitude}` : 'Not found'}</div>
                <div>Source: {job.geocodeSource || 'none'}</div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
            <Button
              variant="outline"
              onClick={onClose}
              disabled={saving}
              className={cn(
                theme === 'light' 
                  ? 'text-gray-700 border-gray-300 hover:bg-gray-100' 
                  : 'text-gray-300 border-gray-600 hover:bg-gray-700'
              )}
            >
              Cancel
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default JobEditModal;
